import { ExtraErrorData, HttpClient } from "@sentry/integrations"
import { isAxiosError } from "axios"

import { IS_PRODUCTION } from "utils/env-management"
import { nonNullable } from "utils/nonNullable"
import { SentryWrapper } from "utils/ThirdParty/Sentry/SentryWrapper"

const blacklistBreadcrumbs: RegExp[] = [
    /mc.yandex.ru/,
    /%cdataLayer.push/,
    /VIWSLR/,
    /mindbox.ru/,
    /sales-ninja.me/,
]
const blacklistFrames: RegExp[] = [/getsitecontrol.com/, /safari-web-extension/, /safari-extension/]
const networkErrorCodesToSkip = [422]

function testArrayRe(arr: RegExp[], value: string) {
    return arr.some((re) => re.test(value))
}

function filterByStatus<T>(event: T, status: number) {
    if (status >= 500) {
        return event
    } else {
        return networkErrorCodesToSkip.includes(status) ? null : event
    }
}

const dsn = process.env.NEXT_PUBLIC_SENTRY_DSN_KEY

if (IS_PRODUCTION && dsn) {
    SentryWrapper.getInstance().init({
        dsn: `https://${dsn}@sentry.io/1528560`,
        release: process.env.NEXT_PUBLIC_LAST_COMMIT || "",
        environment: "prod",
        attachStacktrace: true,
        beforeBreadcrumb: (breadcrumb) => {
            const collectedStrings: string[] = [
                breadcrumb.message,
                breadcrumb.data?.url as string | undefined,
            ].filter(nonNullable)
            if (collectedStrings.some((str) => testArrayRe(blacklistBreadcrumbs, str))) {
                return null
            }
            return breadcrumb
        },
        beforeSend(event, hint) {
            try {
                if (isAxiosError(hint.originalException)) {
                    const axiosError = hint.originalException
                    const response = axiosError.response
                    const baseUrl = axiosError.config?.baseURL
                    if (response && baseUrl && baseUrl.includes(location.host)) {
                        return filterByStatus(event, response.status)
                    }
                    return null
                }

                const exceptionValue = (event?.exception?.values ?? [])[0]
                const frames = exceptionValue?.stacktrace?.frames ?? []
                const rootFrame = frames[0]

                const isEvalError =
                    exceptionValue?.type === "EvalError" &&
                    exceptionValue?.value?.includes("unsafe-eval")

                const isFromCntScript = frames.some((frame) => {
                    const filename = frame.filename ?? ""
                    return filename.includes("x.cnt.my")
                })

                if (isEvalError || isFromCntScript) {
                    return null
                }

                if (testArrayRe(blacklistFrames, rootFrame.filename ?? "")) {
                    return null
                }
            } catch (e) {
                return event
            }

            return event
        },
        ignoreErrors: [
            /processRandomSelector is not defined/,
            /ResizeObserver loop limit exceeded/,
            /ResizeObserver loop completed/,
        ],
        allowUrls: ["alter.ru", "yandexcloud.net/com.psyalter.static.js", "yookassa.ru"],
        denyUrls: [/extensions\//i, /^chrome:\/\//i],
        integrations: [new ExtraErrorData(), new HttpClient()],
    })
}
